import moment from 'moment';
import {OBJECT_ID_PATTERN} from '../constants';

/**
 * CSV import helpers.
 *
 * Column positions are fixed and the first row is always discarded as a header.
 * The file carries no exchange — the backend derives it from the parent and
 * rejects any instrument that does not belong to it.
 */
export const CSV_COLUMNS = [
  'instrumentId',
  'priceAsOf',
  'priceAsOfDate',
  'projectedPriceBefore',
  'projectedPriceBeforeDate',
  'analysis',
];

const SAMPLE_ROW = [
  '6a581e845c6e55b80d72f699',
  '19200',
  '2026-08-17T00:00:00.000Z',
  '21500',
  '2026-11-17T00:00:00.000Z',
  '"<p>Analysis text</p>"',
];

export const SAMPLE_CSV = `${CSV_COLUMNS.join(',')}\n${SAMPLE_ROW.join(',')}\n`;

/** Built in the browser so the template always matches the current contract. */
export const downloadSampleCsv = () => {
  const blob = new Blob([SAMPLE_CSV], {type: 'text/csv;charset=utf-8;'});
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'instrument-recommendation-picks-sample.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/** Minimal RFC-4180 reader: quoted fields, escaped quotes, CRLF or LF. */
export const parseCsv = text => {
  const rows = [];
  let row = [];
  let field = '';
  let quoted = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (quoted) {
      if (char === '"') {
        if (text[index + 1] === '"') {
          field += '"';
          index += 1;
        } else quoted = false;
      } else field += char;
      continue;
    }
    if (char === '"') quoted = true;
    else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && text[index + 1] === '\n') index += 1;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else field += char;
  }
  if (field !== '' || row.length) {
    row.push(field);
    rows.push(row);
  }
  // The parser on the server treats a trailing blank line as an empty pick and
  // rejects the file, so blank rows never leave the browser.
  return rows.filter(entry => entry.some(cell => String(cell).trim() !== ''));
};

const isIsoDate = value =>
  moment(String(value).trim(), moment.ISO_8601, true).isValid();

const isPlainNumber = value =>
  /^-?\d+(\.\d+)?$/.test(String(value).trim()) &&
  Number.isFinite(Number(value));

/**
 * Validates a parsed file row by row. Returns rows carrying their own errors so
 * the preview can show exactly which line to fix.
 */
export const validateCsv = text => {
  const rows = parseCsv(text);
  if (!rows.length) return {fileErrors: ['The file is empty'], rows: []};

  const [header, ...body] = rows;
  const fileErrors = [];

  if (header.length !== CSV_COLUMNS.length)
    fileErrors.push(
      `The header row must have ${
        CSV_COLUMNS.length
      } columns: ${CSV_COLUMNS.join(', ')}`,
    );
  if (!body.length)
    fileErrors.push('No picks found — the first row is read as a header');

  const seen = [];
  const parsedRows = body.map((cells, index) => {
    const [
      instrumentId,
      priceAsOf,
      priceAsOfDate,
      projectedPriceBefore,
      projectedPriceBeforeDate,
      analysis,
    ] = cells.map(cell => String(cell ?? '').trim());
    const errors = [];

    if (cells.length !== CSV_COLUMNS.length)
      errors.push(
        `Expected ${CSV_COLUMNS.length} columns, found ${cells.length}`,
      );
    if (!OBJECT_ID_PATTERN.test(instrumentId))
      errors.push('Column 1 must be a PublicEquity instrument id');
    else if (seen.includes(instrumentId))
      errors.push('Duplicate instrument in file');
    else seen.push(instrumentId);

    if (!isPlainNumber(priceAsOf))
      errors.push('Price as of must be a plain number, e.g. 19200');
    if (!isIsoDate(priceAsOfDate)) errors.push('Price date must be ISO-8601');
    if (!isPlainNumber(projectedPriceBefore))
      errors.push('Target price must be a plain number');
    if (!isIsoDate(projectedPriceBeforeDate))
      errors.push('Target date must be ISO-8601');
    if (!analysis) errors.push('Analysis is empty');

    return {
      line: index + 2,
      instrumentId,
      priceAsOf,
      priceAsOfDate,
      projectedPriceBefore,
      projectedPriceBeforeDate,
      analysis,
      errors,
    };
  });

  return {fileErrors, rows: parsedRows};
};

const escapeCell = value => {
  const cell = String(value ?? '');
  return /[",\n\r]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;
};

/** Re-serializes validated rows so the uploaded file is the cleaned one. */
export const buildCleanedCsv = rows =>
  [
    CSV_COLUMNS.join(','),
    ...rows.map(row =>
      [
        row.instrumentId,
        row.priceAsOf,
        row.priceAsOfDate,
        row.projectedPriceBefore,
        row.projectedPriceBeforeDate,
        row.analysis,
      ]
        .map(escapeCell)
        .join(','),
    ),
  ].join('\n');
